import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Iteration 4 reusable pagination bar.
 *
 * Props:
 *   page           — current page (1-based)
 *   totalPages     — total pages
 *   total          — total record count (for the human summary)
 *   pageSize       — page size
 *   onPageChange   — (nextPage) => void
 *   loading        — optional; disables prev/next while a request is in flight
 *   testid         — optional prefix for data-testid attributes (default 'pagination')
 *
 * Empty state (total===0) renders a compact "No records" summary with disabled
 * controls so the caller can always render it below the table without an if-check.
 */
export default function Pagination({
  page, totalPages, total, pageSize,
  onPageChange, loading = false, testid = "pagination",
}) {
  const safePage = Math.max(1, page || 1);
  const safeTotalPages = Math.max(0, totalPages || 0);
  const hasPrev = safePage > 1 && !loading;
  const hasNext = safePage < safeTotalPages && !loading;

  const first = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const last = total === 0 ? 0 : Math.min(safePage * pageSize, total);

  return (
    <div
      data-testid={testid}
      className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-[#3A4640] bg-[#0B0F0C] text-xs text-[#8A8F98]"
    >
      <div data-testid={`${testid}-summary`} className="font-mono">
        {total === 0
          ? "No records"
          : (
            <>
              <span className="text-[#F8F9FA]">{first}</span>
              {"–"}
              <span className="text-[#F8F9FA]">{last}</span>
              <span className="mx-1">of</span>
              <span className="text-[#F8F9FA]">{total}</span>
            </>
          )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => hasPrev && onPageChange(safePage - 1)}
          disabled={!hasPrev}
          data-testid={`${testid}-prev`}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-heading transition-colors ${
            hasPrev
              ? "border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14]/40 hover:text-[#39FF14]"
              : "border-[#3A4640] text-[#8A8F98] opacity-50 cursor-not-allowed"
          }`}
        >
          <ChevronLeft size={12} /> Previous
        </button>

        <span
          data-testid={`${testid}-current`}
          className="px-2 text-[#F8F9FA] font-mono"
        >
          Page <span className="text-[#39FF14]">{safeTotalPages === 0 ? 0 : safePage}</span> of{" "}
          <span className="text-[#F8F9FA]">{safeTotalPages}</span>
        </span>

        <button
          type="button"
          onClick={() => hasNext && onPageChange(safePage + 1)}
          disabled={!hasNext}
          data-testid={`${testid}-next`}
          className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border text-xs font-heading transition-colors ${
            hasNext
              ? "border-[#3A4640] text-[#F8F9FA] hover:border-[#39FF14]/40 hover:text-[#39FF14]"
              : "border-[#3A4640] text-[#8A8F98] opacity-50 cursor-not-allowed"
          }`}
        >
          Next <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}
